import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import protocolRegistrationService from '../../services/protocolRegistration.service';

const SECONDARY_AUTHORITY_OPTIONS = [
  'UTN (Universal Trial Number)',
  'Sponsor Protocol Number',
  'ClinicalTrials.gov (NCT)',
  'ISRCTN',
  'EU Clinical Trials Register (EUCTR)',
  'CTRI (India)',
  'ACTRN (ANZCTR)',
  'Ethics Committee / IRB',
  'Regulatory Authority',
  'Funding Agency',
  'Other (Specify)',
];

const getIdentifierLabel = (authority) => {
  switch (authority) {
    case 'UTN (Universal Trial Number)':
      return 'UTN Number (required)';
    case 'Sponsor Protocol Number':
      return 'Sponsor Protocol Number (required)';
    case 'ClinicalTrials.gov (NCT)':
      return 'NCT Number (required)';
    case 'ISRCTN':
      return 'ISRCTN Number (required)';
    case 'EU Clinical Trials Register (EUCTR)':
      return 'EUCTR Number (required)';
    case 'CTRI (India)':
      return 'CTRI Number (required)';
    case 'ACTRN (ANZCTR)':
      return 'ACTRN Number (required)';
    case 'Ethics Committee / IRB':
      return 'Ethics / IRB Identifier (required)';
    case 'Regulatory Authority':
      return 'Regulatory Authority Identifier (required)';
    case 'Funding Agency':
      return 'Funding Agency Identifier (required)';
    case 'Other (Specify)':
      return 'Identifier Number (required)';
    default:
      return 'Identifier Number (required)';
  }
};

const emptySecondaryIdentifier = () => ({
  issuing_authority: '',
  issuing_authority_other: '',
  identifier_number: '',
  issuing_body_name: '',
});

const RECRUITMENT_STATUS_OPTIONS = [
  'Pending',
  'Recruiting',
  'Suspended',
  'Complete',
  'Other',
];

const STUDY_TYPE_OPTIONS = ['Interventional', 'Observational'];
const ALLOCATION_METHOD_OPTIONS = ['Randomized', 'Non-randomized', 'Not Applicable'];
const ASSIGNMENT_OPTIONS = ['Single Group', 'Parallel', 'Crossover', 'Factorial', 'Other'];
const MASKING_OPTIONS = [
  'Open-label',
  'Single-blind (participant)',
  'Double-blind (participant, investigator)',
  'Triple-blind (participant, investigator, outcomes assessor)',
  'Quadruple-blind',
  'No masking',
];
const PURPOSE_OPTIONS = [
  'Treatment',
  'Prevention',
  'Diagnostic',
  'Supportive Care',
  'Screening',
  'Health Services Research',
  'Basic Science',
  'Other',
];

const mapCtgovRecruitmentToWho = (status = '') => {
  switch (status) {
    case 'Not yet Recruiting':
      return 'Pending';
    case 'Recruiting':
    case 'Enrolling by invitation':
    case 'Active, not recruiting':
      return 'Recruiting';
    case 'Suspended':
      return 'Suspended';
    case 'Completed':
      return 'Complete';
    default:
      return '';
  }
};

const mapCtgovIpdPlanToWho = (plan = '') => {
  if (plan === 'Yes') return 'Yes';
  if (plan === 'No') return 'No';
  return '';
};

// Helper function to parse eligibility criteria and separate inclusion/exclusion
const parseEligibilityCriteria = (criteriaText) => {
  if (!criteriaText || typeof criteriaText !== 'string') {
    return { inclusion: '', exclusion: '' };
  }

  const text = criteriaText.trim();
  
  // Use a more reliable approach: find the position of "exclusion" (case-insensitive)
  // and check if it's likely the start of an exclusion section
  const lowerText = text.toLowerCase();
  let exclusionStartIndex = -1;
  
  // Look for exclusion patterns - more comprehensive search
  // Search from the beginning for the word "exclusion" as a separate word (not part of another word)
  const exclusionMatches = [
    ...text.matchAll(/\bexclusion\s+criteria:?\s*\n?/gi),
    ...text.matchAll(/\bexclusions?:?\s*\n?/gi),
  ];
  
  // Find the first match that appears after substantial content (likely a section header)
  for (const match of exclusionMatches) {
    const pos = match.index;
    const beforeMatch = text.substring(0, pos).trim();
    
    // Check if this looks like a section break (usually has some content before it)
    // and the match is at the start of a line or after a newline
    const charBefore = pos > 0 ? text[pos - 1] : '\n';
    const isAtLineStart = charBefore === '\n' || charBefore === '\r' || pos === 0;
    
    if (beforeMatch.length > 20 && isAtLineStart) {
      exclusionStartIndex = pos + match[0].length;
      break;
    }
  }
  
  // If no clear section header found, try finding "exclusion" as a standalone word
  if (exclusionStartIndex === -1) {
    const exclusionWordIndex = lowerText.search(/\bexclusion\b/);
    if (exclusionWordIndex !== -1) {
      const beforeExclusion = text.substring(0, exclusionWordIndex).trim();
      const charBefore = exclusionWordIndex > 0 ? text[exclusionWordIndex - 1] : '\n';
      const isAtLineStart = charBefore === '\n' || charBefore === '\r' || exclusionWordIndex === 0;
      
      // If there's substantial content before and it's at line start, it's likely a section
      if (beforeExclusion.length > 50 && isAtLineStart) {
        // Find where the exclusion section actually starts (after any header text)
        const fromExclusion = text.substring(exclusionWordIndex);
        const headerMatch = fromExclusion.match(/exclusion[\s\S]{0,30}:\s*/i);
        if (headerMatch) {
          exclusionStartIndex = exclusionWordIndex + headerMatch[0].length;
        } else {
          // Look for newline after "exclusion" keyword
          const nextNewline = fromExclusion.indexOf('\n');
          if (nextNewline !== -1 && nextNewline < 100) {
            exclusionStartIndex = exclusionWordIndex + nextNewline + 1;
          } else {
            // Skip the word "exclusion" itself
            exclusionStartIndex = exclusionWordIndex + 9; // "exclusion".length
          }
        }
      }
    }
  }
  
  // If we found an exclusion start point, split the text
  if (exclusionStartIndex > 0 && exclusionStartIndex < text.length) {
    const inclusionPart = text.substring(0, exclusionStartIndex).trim();
    const exclusionPart = text.substring(exclusionStartIndex).trim();
    
    // Clean up the exclusion part - remove headers
    let cleanedExclusion = exclusionPart
      .replace(/^exclusion\s+criteria:?\s*/i, '')
      .replace(/^exclusions?:?\s*/i, '')
      .replace(/^exclusion:?\s*/i, '')
      .trim();
    
    // Clean up inclusion part - remove headers
    let cleanedInclusion = inclusionPart
      .replace(/^(?:inclusion\s+criteria|eligibility\s+criteria):?\s*/i, '')
      .replace(/^inclusion:?\s*/i, '')
      .trim();
    
    // Additional cleanup: remove any trailing "Exclusion" that might have been accidentally included
    cleanedInclusion = cleanedInclusion.replace(/\s+exclusion\s*$/i, '').trim();
    
    return {
      inclusion: cleanedInclusion,
      exclusion: cleanedExclusion,
    };
  }
  
  // If no exclusion section found, treat entire text as inclusion only
  let cleanedInclusion = text
    .replace(/^(?:inclusion\s+criteria|eligibility\s+criteria):?\s*/i, '')
    .replace(/^inclusion:?\s*/i, '')
    .trim();
  
  return {
    inclusion: cleanedInclusion,
    exclusion: '',
  };
};

const initialForm = {
  primary_registry: {
    name: '',
    unique_id_number: '',
  },
  registration_date: '',
  date_of_first_enrollment: '',
  secondary_identifiers: [],
  sources_of_support: '',
  primary_sponsor: '',
  secondary_sponsors: '',
  contact_for_public_queries: {
    email: '',
    telephone: '',
    postal_address: '',
    recruitment_status: '',
  },
  contact_for_scientific_queries: {
    principal_investigator: {
      name_and_title: '',
      email: '',
      telephone: '',
      postal_address: '',
      affiliation: '',
    },
    scientific_contact: {
      email: '',
      telephone: '',
      postal_address: '',
      affiliation: '',
    },
  },
  public_title: '',
  scientific_title: '',
  countries_of_recruitment: [],
  health_conditions: '',
  interventions: [{ name: '', description: '' }],
  inclusion_criteria: '',
  exclusion_criteria: '',
  study_type: {
    type: '',
    allocation_method: '',
    masking: '',
    assignment: '',
    purpose: '',
    phase: '',
    allocation_concealment_and_sequence_generation: '',
  },
  sample_size_planned: '',
  sample_size_enrolled: '',
  recruitment_status_overall: '',
  primary_outcomes: [{ name: '', metric: '', timepoint: '' }],
  secondary_outcomes: [{ name: '', metric: '', timepoint: '' }],
  ethics_review: {
    status: '',
    approval_date: '',
    committee_details: '',
  },
  completion_date: '',
  summary_results: {
    results_posting_date: '',
    first_publication_date: '',
    results_urls_text: '',
    baseline_characteristics: '',
    participant_flow: '',
    adverse_events: '',
    outcome_measures: '',
    protocol_url: '',
    brief_summary: '',
  },
  ipd_sharing: {
    plan: '',
    description: '',
  },
};

/**
 * XML utility functions for ClinicalTrials.gov XSD conversion from WHO form data
 */

// Escape XML special characters
function escapeXml(unsafe) {
  if (unsafe === null || unsafe === undefined) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Convert Yes/No to lowercase yes/no
function yesNoToXml(value) {
  if (value === 'Yes') return 'yes';
  if (value === 'No') return 'no';
  return '';
}

// Format date to YYYY-MM format
function formatDateForXml(dateStr) {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}$/.test(dateStr)) return dateStr;
  try {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  } catch {
    return dateStr;
  }
}

// Split comma-separated or newline-separated string into array
function splitList(value = '') {
  if (!value) return [];
  return value
    .split(/[,\n]/)
    .map((v) => v.trim())
    .filter(Boolean);
}

// Map WHO recruitment status to ClinicalTrials.gov status
function mapWhoStatusToCtgov(status) {
  const statusMap = {
    'Pending': 'Not yet recruiting',
    'Recruiting': 'Recruiting',
    'Suspended': 'Suspended',
    'Complete': 'Completed',
    'Other': 'Recruiting',
  };
  return statusMap[status] || status;
}

// Map WHO masking to ClinicalTrials.gov masking format
function mapWhoMaskingToCtgov(masking) {
  if (!masking) return { no_masking: 'yes' };
  const maskingLower = masking.toLowerCase();
  return {
    no_masking: maskingLower.includes('no masking') || maskingLower.includes('open-label') ? 'yes' : 'no',
    masked_subject: maskingLower.includes('participant') || maskingLower.includes('single-blind') || maskingLower.includes('double-blind') || maskingLower.includes('triple-blind') || maskingLower.includes('quadruple-blind') ? 'yes' : 'no',
    masked_caregiver: maskingLower.includes('caregiver') || maskingLower.includes('double-blind') || maskingLower.includes('triple-blind') || maskingLower.includes('quadruple-blind') ? 'yes' : 'no',
    masked_investigator: maskingLower.includes('investigator') || maskingLower.includes('double-blind') || maskingLower.includes('triple-blind') || maskingLower.includes('quadruple-blind') ? 'yes' : 'no',
    masked_assessor: maskingLower.includes('assessor') || maskingLower.includes('outcomes') || maskingLower.includes('triple-blind') || maskingLower.includes('quadruple-blind') ? 'yes' : 'no',
  };
}

// Map WHO assignment to ClinicalTrials.gov assignment
function mapWhoAssignmentToCtgov(assignment) {
  const assignmentMap = {
    'Single Group': 'Single Group Assignment',
    'Parallel': 'Parallel Assignment',
    'Crossover': 'Crossover Assignment',
    'Factorial': 'Factorial Assignment',
    'Other': 'Parallel Assignment',
  };
  return assignmentMap[assignment] || assignment;
}

// Map WHO allocation method to ClinicalTrials.gov allocation
function mapWhoAllocationToCtgov(allocation) {
  const allocationMap = {
    'Randomized': 'Randomized',
    'Non-randomized': 'Non-randomized',
    'Not Applicable': 'N/A',
  };
  return allocationMap[allocation] || allocation;
}

// Parse name string into first, middle, last
function parseName(nameStr) {
  if (!nameStr) return { first: '', middle: '', last: '' };
  const parts = nameStr.trim().split(/\s+/);
  if (parts.length === 1) return { first: '', middle: '', last: parts[0] };
  if (parts.length === 2) return { first: parts[0], middle: '', last: parts[1] };
  return {
    first: parts[0],
    middle: parts.slice(1, -1).join(' '),
    last: parts[parts.length - 1],
  };
}

// Convert WHO form data to ClinicalTrials.gov XSD-compliant XML
function convertWhoFormDataToXML(formData, orgName = '') {
  const esc = escapeXml;
  const yesNo = yesNoToXml;
  const fmtDate = formatDateForXml;

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<study_collection xmlns="http://clinicaltrials.gov/prs" xmlns:rrs="http://clinicaltrials.gov/rrs">\n';
  xml += '  <clinical_study>\n';

  // id_info
  xml += '    <id_info>\n';
  xml += `      <org_name>${esc(orgName || '')}</org_name>\n`;
  xml += `      <org_study_id>${esc(formData.primary_registry?.unique_id_number || '')}</org_study_id>\n`;
  
  // secondary_id from WHO secondary_identifiers
  (formData.secondary_identifiers || []).forEach(secId => {
    if (secId.identifier_number) {
      xml += '      <secondary_id>\n';
      xml += `        <id>${esc(secId.identifier_number)}</id>\n`;
      if (secId.issuing_authority && secId.issuing_authority !== 'Other (Specify)') {
        const idTypeMap = {
          'UTN (Universal Trial Number)': 'Other Identifier',
          'Sponsor Protocol Number': 'Other Identifier',
          'ClinicalTrials.gov (NCT)': 'Registry Identifier',
          'ISRCTN': 'Registry Identifier',
          'EU Clinical Trials Register (EUCTR)': 'EudraCT Number',
          'CTRI (India)': 'Registry Identifier',
          'ACTRN (ANZCTR)': 'Registry Identifier',
          'Ethics Committee / IRB': 'Other Identifier',
          'Regulatory Authority': 'Other Identifier',
          'Funding Agency': 'Other Grant/Funding Number',
        };
        xml += `        <id_type>${esc(idTypeMap[secId.issuing_authority] || 'Other Identifier')}</id_type>\n`;
        if (secId.issuing_body_name) {
          xml += `        <id_domain>${esc(secId.issuing_body_name)}</id_domain>\n`;
        }
      } else if (secId.issuing_authority_other) {
        xml += '        <id_type>Other Identifier</id_type>\n';
        xml += `        <id_domain>${esc(secId.issuing_authority_other)}</id_domain>\n`;
      }
      xml += '      </secondary_id>\n';
    }
  });
  xml += '    </id_info>\n';

  // brief_title (from public_title)
  if (formData.public_title) {
    xml += `    <brief_title>${esc(formData.public_title)}</brief_title>\n`;
  }

  // official_title (from scientific_title)
  if (formData.scientific_title) {
    xml += `    <official_title>${esc(formData.scientific_title)}</official_title>\n`;
  }

  // sponsors
  if (formData.primary_sponsor) {
    xml += '    <sponsors>\n';
    xml += '      <lead_sponsor>\n';
    xml += `        <agency>${esc(formData.primary_sponsor)}</agency>\n`;
    xml += '      </lead_sponsor>\n';
    
    const secondarySponsors = splitList(formData.secondary_sponsors || '');
    secondarySponsors.forEach(sponsor => {
      if (sponsor) {
        xml += '      <collaborator>\n';
        xml += `        <agency>${esc(sponsor)}</agency>\n`;
        xml += '      </collaborator>\n';
      }
    });

    const pi = formData.contact_for_scientific_queries?.principal_investigator;
    if (pi?.name_and_title) {
      xml += '      <resp_party>\n';
      xml += '        <resp_party_type>Principal Investigator</resp_party_type>\n';
      if (pi.affiliation) {
        xml += `        <investigator_affiliation>${esc(pi.affiliation)}</investigator_affiliation>\n`;
      }
      xml += '      </resp_party>\n';
    }
    xml += '    </sponsors>\n';
  }

  // brief_summary
  if (formData.summary_results?.brief_summary) {
    xml += '    <brief_summary>\n';
    xml += `      <textblock>${esc(formData.summary_results.brief_summary)}</textblock>\n`;
    xml += '    </brief_summary>\n';
  }

  // overall_status
  if (formData.recruitment_status_overall || formData.contact_for_public_queries?.recruitment_status) {
    const status = formData.recruitment_status_overall || formData.contact_for_public_queries.recruitment_status;
    xml += `    <overall_status>${esc(mapWhoStatusToCtgov(status))}</overall_status>\n`;
  }

  // start_date
  if (formData.date_of_first_enrollment) {
    xml += `    <start_date>${fmtDate(formData.date_of_first_enrollment)}</start_date>\n`;
    xml += '    <start_date_type>Anticipated</start_date_type>\n';
  }

  // completion_date
  if (formData.completion_date) {
    xml += `    <last_follow_up_date>${fmtDate(formData.completion_date)}</last_follow_up_date>\n`;
    xml += '    <last_follow_up_date_type>Anticipated</last_follow_up_date_type>\n';
    xml += `    <primary_compl_date>${fmtDate(formData.completion_date)}</primary_compl_date>\n`;
    xml += '    <primary_compl_date_type>Anticipated</primary_compl_date_type>\n';
  }

  // study_design
  const studyType = formData.study_type?.type || 'Interventional';
  xml += '    <study_design>\n';
  xml += `      <study_type>${esc(studyType)}</study_type>\n`;

  if (studyType === 'Interventional' && formData.study_type) {
    const st = formData.study_type;
    xml += '      <interventional_design>\n';
    
    if (st.purpose) {
      xml += `        <interventional_subtype>${esc(st.purpose)}</interventional_subtype>\n`;
    }
    
    if (st.phase) {
      xml += `        <phase>${esc(st.phase)}</phase>\n`;
    } else {
      xml += '        <phase>N/A</phase>\n';
    }

    if (st.assignment) {
      xml += `        <assignment>${esc(mapWhoAssignmentToCtgov(st.assignment))}</assignment>\n`;
    }

    if (st.allocation_method) {
      xml += `        <allocation>${esc(mapWhoAllocationToCtgov(st.allocation_method))}</allocation>\n`;
    }

    if (st.masking) {
      const maskingMap = mapWhoMaskingToCtgov(st.masking);
      Object.entries(maskingMap).forEach(([key, value]) => {
        if (key === 'no_masking') {
          xml += `        <${key}>${value}</${key}>\n`;
        } else if (value === 'yes') {
          xml += `        <${key}>${value}</${key}>\n`;
        }
      });
    } else {
      xml += '        <no_masking>yes</no_masking>\n';
    }

    xml += '      </interventional_design>\n';
  } else if (studyType === 'Observational') {
    xml += '      <observational_design>\n';
    xml += '        <observational_study_design>Other</observational_study_design>\n';
    xml += '        <biospecimen_retention>None Retained</biospecimen_retention>\n';
    xml += '        <timing>Prospective</timing>\n';
    xml += '        <number_of_groups>1</number_of_groups>\n';
    xml += '      </observational_design>\n';
  }

  xml += '    </study_design>\n';

  // primary_outcome
  (formData.primary_outcomes || []).forEach(outcome => {
    if (outcome.name) {
      xml += '    <primary_outcome>\n';
      xml += `      <outcome_measure>${esc(outcome.name)}</outcome_measure>\n`;
      if (outcome.timepoint) {
        xml += `      <outcome_time_frame>${esc(outcome.timepoint)}</outcome_time_frame>\n`;
      }
      if (outcome.metric) {
        xml += '      <outcome_description>\n';
        xml += `        <textblock>${esc(outcome.metric)}</textblock>\n`;
        xml += '      </outcome_description>\n';
      }
      xml += '    </primary_outcome>\n';
    }
  });

  // secondary_outcome
  (formData.secondary_outcomes || []).forEach(outcome => {
    if (outcome.name) {
      xml += '    <secondary_outcome>\n';
      xml += `      <outcome_measure>${esc(outcome.name)}</outcome_measure>\n`;
      if (outcome.timepoint) {
        xml += `      <outcome_time_frame>${esc(outcome.timepoint)}</outcome_time_frame>\n`;
      }
      if (outcome.metric) {
        xml += '      <outcome_description>\n';
        xml += `        <textblock>${esc(outcome.metric)}</textblock>\n`;
        xml += '      </outcome_description>\n';
      }
      xml += '    </secondary_outcome>\n';
    }
  });

  // enrollment
  if (formData.sample_size_planned) {
    xml += `    <enrollment>${esc(String(formData.sample_size_planned))}</enrollment>\n`;
    xml += '    <enrollment_type>Anticipated</enrollment_type>\n';
  }

  // condition
  if (formData.health_conditions) {
    const conditions = splitList(formData.health_conditions);
    conditions.forEach(condition => {
      if (condition) {
        xml += `    <condition>${esc(condition)}</condition>\n`;
      }
    });
  }

  // intervention
  (formData.interventions || []).forEach(intervention => {
    if (intervention.name) {
      xml += '    <intervention>\n';
      xml += '      <intervention_type>Other</intervention_type>\n';
      xml += `      <intervention_name>${esc(intervention.name)}</intervention_name>\n`;
      if (intervention.description) {
        xml += '      <intervention_description>\n';
        xml += `        <textblock>${esc(intervention.description)}</textblock>\n`;
        xml += '      </intervention_description>\n';
      }
      xml += '    </intervention>\n';
    }
  });

  // eligibility
  if (formData.inclusion_criteria || formData.exclusion_criteria) {
    xml += '    <eligibility>\n';
    
    const criteria = [];
    if (formData.inclusion_criteria) {
      criteria.push('Inclusion Criteria:\n' + formData.inclusion_criteria);
    }
    if (formData.exclusion_criteria) {
      criteria.push('Exclusion Criteria:\n' + formData.exclusion_criteria);
    }
    
    xml += '      <criteria>\n';
    xml += `        <textblock>${esc(criteria.join('\n\n'))}</textblock>\n`;
    xml += '      </criteria>\n';
    
    xml += '      <gender>All</gender>\n';
    xml += '      <minimum_age>N/A</minimum_age>\n';
    xml += '      <maximum_age>N/A</maximum_age>\n';
    
    xml += '    </eligibility>\n';
  }

  // overall_official
  const pi = formData.contact_for_scientific_queries?.principal_investigator;
  if (pi?.name_and_title) {
    xml += '    <overall_official>\n';
    const nameParts = parseName(pi.name_and_title);
    if (nameParts.first) xml += `      <first_name>${esc(nameParts.first)}</first_name>\n`;
    if (nameParts.middle) xml += `      <middle_name>${esc(nameParts.middle)}</middle_name>\n`;
    if (nameParts.last) xml += `      <last_name>${esc(nameParts.last)}</last_name>\n`;
    xml += '      <role>Principal Investigator</role>\n';
    if (pi.affiliation) xml += `      <affiliation>${esc(pi.affiliation)}</affiliation>\n`;
    xml += '    </overall_official>\n';
  }

  // overall_contact
  const publicContact = formData.contact_for_public_queries;
  if (publicContact?.email) {
    xml += '    <overall_contact>\n';
    xml += `      <email>${esc(publicContact.email)}</email>\n`;
    if (publicContact.telephone) xml += `      <phone>${esc(publicContact.telephone)}</phone>\n`;
    xml += '    </overall_contact>\n';
  }

  // location
  const countries = formData.countries_of_recruitment || [];
  if (countries.length > 0) {
    countries.forEach(country => {
      if (country) {
        xml += '    <location>\n';
        xml += '      <facility>\n';
        xml += `        <name>${esc(country)}</name>\n`;
        xml += '        <address>\n';
        xml += `          <country>${esc(country)}</country>\n`;
        xml += '        </address>\n';
        xml += '      </facility>\n';
        xml += '      <status>Recruiting</status>\n';
        xml += '    </location>\n';
      }
    });
  }

  // ipd_sharing_statement
  if (formData.ipd_sharing?.plan) {
    xml += '    <ipd_sharing_statement>\n';
    xml += `      <sharing_ipd>${yesNo(formData.ipd_sharing.plan)}</sharing_ipd>\n`;
    if (formData.ipd_sharing.description) {
      xml += '      <ipd_description>\n';
      xml += `        <textblock>${esc(formData.ipd_sharing.description)}</textblock>\n`;
      xml += '      </ipd_description>\n';
    }
    xml += '    </ipd_sharing_statement>\n';
  }

  // link
  if (formData.summary_results?.results_urls_text) {
    const urls = splitList(formData.summary_results.results_urls_text);
    urls.forEach(url => {
      if (url) {
        xml += '    <link>\n';
        xml += `      <url>${esc(url)}</url>\n`;
        xml += '      <description>Results</description>\n';
        xml += '    </link>\n';
      }
    });
  }

  if (formData.summary_results?.protocol_url) {
    xml += '    <link>\n';
    xml += `      <url>${esc(formData.summary_results.protocol_url)}</url>\n`;
    xml += '      <description>Protocol</description>\n';
    xml += '    </link>\n';
  }

  xml += '  </clinical_study>\n';
  xml += '</study_collection>';

  return xml;
}

const WHORegistry = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formatDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      // If it's already a string in YYYY-MM-DD format, return it
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
      // Try to parse and format
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        return d.toISOString().split('T')[0];
      }
    }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await protocolRegistrationService.getByProject(projectId);
        // Extract WHO-specific data if it exists
        if (data?.who_registry) {
          const who = data.who_registry;
          setFormData({
            primary_registry: {
              name: who.primary_registry?.name || '',
              unique_id_number: who.primary_registry?.unique_id_number || '',
            },
            registration_date: formatDate(who.registration_date) || '',
            date_of_first_enrollment: formatDate(who.date_of_first_enrollment) || '',
            secondary_identifiers:
              who.secondary_identifiers?.length
                ? who.secondary_identifiers.map((item) => ({
                    issuing_authority: item.issuing_authority || '',
                    issuing_authority_other: item.issuing_authority_other || '',
                    identifier_number: item.identifier_number || '',
                    issuing_body_name: item.issuing_body_name || '',
                  }))
                : [],
            sources_of_support: who.sources_of_support || '',
            primary_sponsor: who.primary_sponsor || '',
            secondary_sponsors: who.secondary_sponsors || '',
            contact_for_public_queries: {
              email: who.contact_for_public_queries?.email || '',
              telephone: who.contact_for_public_queries?.telephone || '',
              postal_address: who.contact_for_public_queries?.postal_address || '',
              recruitment_status: who.contact_for_public_queries?.recruitment_status || '',
            },
            contact_for_scientific_queries: {
              principal_investigator: {
                name_and_title: who.contact_for_scientific_queries?.principal_investigator?.name_and_title || '',
                email: who.contact_for_scientific_queries?.principal_investigator?.email || '',
                telephone: who.contact_for_scientific_queries?.principal_investigator?.telephone || '',
                postal_address: who.contact_for_scientific_queries?.principal_investigator?.postal_address || '',
                affiliation: who.contact_for_scientific_queries?.principal_investigator?.affiliation || '',
              },
              scientific_contact: {
                email: who.contact_for_scientific_queries?.scientific_contact?.email || '',
                telephone: who.contact_for_scientific_queries?.scientific_contact?.telephone || '',
                postal_address: who.contact_for_scientific_queries?.scientific_contact?.postal_address || '',
                affiliation: who.contact_for_scientific_queries?.scientific_contact?.affiliation || '',
              },
            },
            public_title: who.public_title || '',
            scientific_title: who.scientific_title || '',
            countries_of_recruitment: who.countries_of_recruitment || [],
            health_conditions: who.health_conditions || '',
            interventions: who.interventions?.length 
              ? who.interventions.map((item) => ({
                  name: item.name || '',
                  description: item.description || '',
                }))
              : [{ name: '', description: '' }],
            inclusion_criteria: who.inclusion_criteria || '',
            exclusion_criteria: who.exclusion_criteria || '',
            study_type: {
              type: who.study_type?.type || '',
              allocation_method: who.study_type?.allocation_method || '',
              masking: who.study_type?.masking || '',
              assignment: who.study_type?.assignment || '',
              purpose: who.study_type?.purpose || '',
              phase: who.study_type?.phase || '',
              allocation_concealment_and_sequence_generation:
                who.study_type?.allocation_concealment_and_sequence_generation || '',
            },
            sample_size_planned: (who.sample_size_planned !== undefined && who.sample_size_planned !== null)
              ? String(who.sample_size_planned)
              : '',
            sample_size_enrolled: (who.sample_size_enrolled !== undefined && who.sample_size_enrolled !== null)
              ? String(who.sample_size_enrolled)
              : '',
            recruitment_status_overall: who.recruitment_status || '',
            primary_outcomes: who.primary_outcomes?.length
              ? who.primary_outcomes.map((item) => ({
                  name: item.name || '',
                  metric: item.metric || '',
                  timepoint: item.timepoint || '',
                }))
              : [{ name: '', metric: '', timepoint: '' }],
            secondary_outcomes: who.secondary_outcomes?.length
              ? who.secondary_outcomes.map((item) => ({
                  name: item.name || '',
                  metric: item.metric || '',
                  timepoint: item.timepoint || '',
                }))
              : [{ name: '', metric: '', timepoint: '' }],
            ethics_review: {
              status: who.ethics_review?.status || '',
              approval_date: formatDate(who.ethics_review?.approval_date) || '',
              committee_details: who.ethics_review?.committee_details || '',
            },
            completion_date: formatDate(who.completion_date) || '',
            summary_results: {
              results_posting_date: formatDate(who.summary_results?.results_posting_date) || '',
              first_publication_date: formatDate(who.summary_results?.first_publication_date) || '',
              results_urls_text: (who.summary_results?.results_urls || []).join('\n'),
              baseline_characteristics: who.summary_results?.baseline_characteristics || '',
              participant_flow: who.summary_results?.participant_flow || '',
              adverse_events: who.summary_results?.adverse_events || '',
              outcome_measures: who.summary_results?.outcome_measures || '',
              protocol_url: who.summary_results?.protocol_url || '',
              brief_summary: who.summary_results?.brief_summary || '',
            },
            ipd_sharing: {
              plan: who.ipd_sharing?.plan || '',
              description: who.ipd_sharing?.description || '',
            },
          });
        } else {
          setFormData(initialForm);
        }
      } catch (err) {
        console.error('Error loading WHO registry data:', err);
        // Even if loading fails, set form to initial state so fields are editable
        setFormData(initialForm);
        // Optionally show error, but don't block the form
        // setError(err?.message || 'Failed to load existing data. You can still enter new data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const handlePrimaryRegistryChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      primary_registry: {
        ...prev.primary_registry,
        [field]: value,
      },
    }));
  };

  const handleRegistrationDateChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      registration_date: event.target.value,
    }));
  };

  const handleFirstEnrollmentDateChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      date_of_first_enrollment: event.target.value,
    }));
  };

  const handleSourcesOfSupportChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      sources_of_support: event.target.value,
    }));
  };

  const handlePrimarySponsorChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      primary_sponsor: event.target.value,
    }));
  };

  const handleSecondarySponsorsChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      secondary_sponsors: event.target.value,
    }));
  };

  const handleContactForPublicQueriesChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      contact_for_public_queries: {
        ...prev.contact_for_public_queries,
        [field]: value,
      },
    }));
  };

  const handlePrincipalInvestigatorChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      contact_for_scientific_queries: {
        ...prev.contact_for_scientific_queries,
        principal_investigator: {
          ...prev.contact_for_scientific_queries.principal_investigator,
          [field]: value,
        },
      },
    }));
  };

  const handleScientificContactChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      contact_for_scientific_queries: {
        ...prev.contact_for_scientific_queries,
        scientific_contact: {
          ...prev.contact_for_scientific_queries.scientific_contact,
          [field]: value,
        },
      },
    }));
  };

  const handlePublicTitleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      public_title: event.target.value,
    }));
  };

  const handleScientificTitleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      scientific_title: event.target.value,
    }));
  };

  const [countryInput, setCountryInput] = useState('');

  const handleAddCountry = () => {
    if (countryInput.trim() && !formData.countries_of_recruitment.includes(countryInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        countries_of_recruitment: [...prev.countries_of_recruitment, countryInput.trim()],
      }));
      setCountryInput('');
    }
  };

  const handleRemoveCountry = (countryToRemove) => () => {
    setFormData((prev) => ({
      ...prev,
      countries_of_recruitment: prev.countries_of_recruitment.filter((c) => c !== countryToRemove),
    }));
  };

  const handleHealthConditionsChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      health_conditions: event.target.value,
    }));
  };

  const handleInterventionChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.interventions];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, interventions: next };
    });
  };

  const handleAddIntervention = () => {
    setFormData((prev) => ({
      ...prev,
      interventions: [...prev.interventions, { name: '', description: '' }],
    }));
  };

  const handleRemoveIntervention = (index) => () => {
    setFormData((prev) => {
      const next = prev.interventions.filter((_, i) => i !== index);
      return {
        ...prev,
        interventions: next.length ? next : [{ name: '', description: '' }],
      };
    });
  };

  const handleInclusionCriteriaChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      inclusion_criteria: event.target.value,
    }));
  };

  const handleExclusionCriteriaChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      exclusion_criteria: event.target.value,
    }));
  };

  const handleSampleSizeChange = (field) => (event) => {
    const value = event.target.value;
    // Keep as string in state (for easier input handling), convert to number on save
    if (value === '' || /^\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleRecruitmentStatusOverallChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      recruitment_status_overall: value,
    }));
  };

  const handlePrimaryOutcomeChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.primary_outcomes];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, primary_outcomes: next };
    });
  };

  const handleSecondaryOutcomeChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.secondary_outcomes];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, secondary_outcomes: next };
    });
  };

  const handleAddPrimaryOutcome = () => {
    setFormData((prev) => ({
      ...prev,
      primary_outcomes: [...prev.primary_outcomes, { name: '', metric: '', timepoint: '' }],
    }));
  };

  const handleRemovePrimaryOutcome = (index) => () => {
    setFormData((prev) => {
      const next = prev.primary_outcomes.filter((_, i) => i !== index);
      return {
        ...prev,
        primary_outcomes: next.length ? next : [{ name: '', metric: '', timepoint: '' }],
      };
    });
  };

  const handleAddSecondaryOutcome = () => {
    setFormData((prev) => ({
      ...prev,
      secondary_outcomes: [...prev.secondary_outcomes, { name: '', metric: '', timepoint: '' }],
    }));
  };

  const handleRemoveSecondaryOutcome = (index) => () => {
    setFormData((prev) => {
      const next = prev.secondary_outcomes.filter((_, i) => i !== index);
      return {
        ...prev,
        secondary_outcomes: next.length ? next : [{ name: '', metric: '', timepoint: '' }],
      };
    });
  };

  const handleEthicsReviewChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      ethics_review: {
        ...prev.ethics_review,
        [field]: value,
      },
    }));
  };

  const handleCompletionDateChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      completion_date: event.target.value,
    }));
  };

  const handleSummaryResultsChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      summary_results: {
        ...prev.summary_results,
        [field]: value,
      },
    }));
  };

  const handleIpdSharingChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      ipd_sharing: {
        ...prev.ipd_sharing,
        [field]: value,
      },
    }));
  };

  const handleAutoPopulateFromCoreDatasheet = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    setAutoPopulating(true);
    try {
      const response = await protocolRegistrationService.autoPopulateRegistry(
        projectId,
        'who'
      );

      if (response.success && response.data) {
        // Merge auto-populated data with existing form data
        const mergedData = { ...formData };
        
        // Deep merge function for nested objects
        const deepMerge = (target, source) => {
          Object.keys(source).forEach(key => {
            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
              if (!target[key]) target[key] = {};
              deepMerge(target[key], source[key]);
            } else if (source[key] !== null && source[key] !== undefined && source[key] !== '') {
              if (Array.isArray(source[key]) && source[key].length > 0) {
                target[key] = source[key];
              } else if (!Array.isArray(source[key])) {
                target[key] = source[key];
              }
            }
          });
        };

        deepMerge(mergedData, response.data);
        setFormData(mergedData);
        showSnackbar('Form auto-populated from Core Datasheet. Review and save when ready.', 'success');
      } else {
        showSnackbar(response.message || 'Failed to auto-populate from Core Datasheet', 'error');
      }
    } catch (error) {
      console.error('Error auto-populating from Core Datasheet:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to auto-populate from Core Datasheet',
        'error'
      );
    } finally {
      setAutoPopulating(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAutoFill = async () => {
    if (!projectId) {
      setError('Please select a project before auto-fill.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await protocolRegistrationService.autoFill(projectId);

      setFormData((prev) => {
        // Derive countries of recruitment
        const facilities = data?.contacts_locations_investigators?.facilities || [];
        const countries = Array.from(
          new Set(
            facilities
              .map((f) => f?.country)
              .filter((c) => typeof c === 'string' && c.trim().length > 0)
          )
        );

        // Derive health conditions string
        const primaryCondition = data?.condition_keywords?.primary_condition || '';
        const keywordsArr = Array.isArray(data?.condition_keywords?.keywords)
          ? data.condition_keywords.keywords.filter((k) => k && k.trim().length > 0)
          : [];
        const healthConditionsCombined = [primaryCondition, keywordsArr.join(', ')]
          .filter((v) => v && v.trim().length > 0)
          .join('; ');

      const mapOutcomes = (list) =>
        Array.isArray(list) && list.length
          ? list.map((o) => ({
              name: o?.title || '',
              metric: o?.description || '',
              timepoint: o?.timeframe || '',
            }))
          : null;

        const mappedPrimaryOutcomes = mapOutcomes(data?.outcomes?.primary);
        const mappedSecondaryOutcomes = mapOutcomes(data?.outcomes?.secondary);

        const ctInterventions = data?.arms_groups_interventions?.interventions || [];
        const mappedInterventions =
          Array.isArray(ctInterventions) && ctInterventions.length
            ? ctInterventions.map((i) => ({
                name: i?.name || '',
                description: i?.description || '',
              }))
            : null;

        return {
          ...prev,
          public_title:
            data?.study_description?.brief_summary ||
            prev.public_title,
          scientific_title:
            data?.study_identification?.official_title ||
            prev.scientific_title,
          date_of_first_enrollment:
            data?.study_status?.study_start_date
              ? formatDate(data.study_status.study_start_date)
              : prev.date_of_first_enrollment,
          completion_date:
            data?.study_status?.study_completion_date
              ? formatDate(data.study_status.study_completion_date)
              : prev.completion_date,
          recruitment_status_overall:
            mapCtgovRecruitmentToWho(data?.study_status?.overall_recruitment_status) ||
            prev.recruitment_status_overall,
          sample_size_planned:
            data?.study_design?.interventional?.enrollment != null
              ? String(data.study_design.interventional.enrollment)
              : data?.study_design?.observational?.enrollment != null
              ? String(data.study_design.observational.enrollment)
              : prev.sample_size_planned,
          countries_of_recruitment:
            countries.length > 0 ? countries : prev.countries_of_recruitment,
          health_conditions: healthConditionsCombined || prev.health_conditions,
          interventions: mappedInterventions || prev.interventions,
          // Use separate inclusion_criteria and exclusion_criteria if available, otherwise fallback to parsing combined eligibility_criteria
          inclusion_criteria: data?.eligibility?.inclusion_criteria || 
            (data?.eligibility?.eligibility_criteria 
              ? parseEligibilityCriteria(data.eligibility.eligibility_criteria).inclusion 
              : null) || 
            prev.inclusion_criteria,
          exclusion_criteria: data?.eligibility?.exclusion_criteria || 
            (data?.eligibility?.eligibility_criteria 
              ? parseEligibilityCriteria(data.eligibility.eligibility_criteria).exclusion 
              : null) || 
            prev.exclusion_criteria,
          primary_outcomes: mappedPrimaryOutcomes || prev.primary_outcomes,
          secondary_outcomes: mappedSecondaryOutcomes || prev.secondary_outcomes,
          study_type: {
            ...prev.study_type,
            type: data?.study_identification?.study_type || prev.study_type.type,
            allocation_method:
              data?.study_design?.interventional?.allocation || prev.study_type.allocation_method,
            masking:
              data?.study_design?.interventional?.masking?.description ||
              prev.study_type.masking,
            assignment:
              data?.study_design?.interventional?.study_model || prev.study_type.assignment,
            purpose:
              data?.study_design?.interventional?.primary_purpose || prev.study_type.purpose,
            phase:
              data?.study_design?.interventional?.study_phase || prev.study_type.phase,
          },
          ipd_sharing: {
            ...prev.ipd_sharing,
            plan: mapCtgovIpdPlanToWho(data?.ipd_sharing?.plan) || prev.ipd_sharing.plan,
            description: data?.ipd_sharing?.description || prev.ipd_sharing.description,
          },
        };
      });

      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Failed to auto-fill from protocol');
    } finally {
      setLoading(false);
    }
  };

  const handleStudyTypeChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const nextStudyType = {
        ...prev.study_type,
        [field]: value,
      };
      // If allocation method is changed away from Randomized, clear the randomization details
      if (field === 'allocation_method' && value !== 'Randomized') {
        nextStudyType.allocation_concealment_and_sequence_generation = '';
      }
      return {
        ...prev,
        study_type: nextStudyType,
      };
    });
  };

  const handleSecondaryChange = (authority, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = prev.secondary_identifiers.map((item) => {
        if (item.issuing_authority === authority) {
          return {
            ...item,
            [field]: value,
          };
        }
        return item;
      });
      return { ...prev, secondary_identifiers: next };
    });
  };

  const handleAuthorityToggle = (authority) => (event) => {
    const { checked } = event.target;
    setFormData((prev) => {
      const existing = prev.secondary_identifiers || [];
      const alreadySelected = existing.some((item) => item.issuing_authority === authority);

      if (checked && !alreadySelected) {
        // Add a new identifier block for this authority
        return {
          ...prev,
          secondary_identifiers: [
            ...existing,
            {
              issuing_authority: authority,
              issuing_authority_other: '',
              identifier_number: '',
              issuing_body_name: '',
            },
          ],
        };
      }

      if (!checked && alreadySelected) {
        // Remove the identifier block for this authority
        const next = existing.filter((item) => item.issuing_authority !== authority);
        return {
          ...prev,
          secondary_identifiers: next,
        };
      }

      return prev;
    });
  };

  const handleExportXML = async () => {
    try {
      if (!projectId) {
        setError('Please select a project before exporting XML.');
        return;
      }

      // Get org name - in real implementation, this might come from user settings or project data
      const orgName = formData.primary_registry?.unique_id_number ? '' : '';
      
      // Convert WHO form data to ClinicalTrials.gov XML
      const xmlContent = convertWhoFormDataToXML(formData, orgName);
      
      // Save XML to database
      try {
        await protocolRegistrationService.saveXML(projectId, xmlContent, 'who');
      } catch (saveError) {
        console.error('Error saving XML to database:', saveError);
        // Continue with download even if save fails
      }
      
      // Create a blob with the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on protocol ID or use default
      const protocolId = formData.primary_registry?.unique_id_number || 'who_trial';
      const filename = `who_clinical_trials_gov_${protocolId}_${new Date().toISOString().split('T')[0]}.xml`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('XML file exported and saved successfully!');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error exporting XML:', err);
      setError('Failed to export XML. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!projectId) {
      setError('Please select a project before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Get existing data first
      let existingData = {};
      try {
        existingData = (await protocolRegistrationService.getByProject(projectId)) || {};
      } catch (err) {
        // If no existing data, that's okay - we'll create new
        console.log('No existing protocol registration data found, creating new');
      }

      const cleanedSecondary = (formData.secondary_identifiers || [])
        .filter((item) =>
          (item.issuing_authority && item.issuing_authority.trim() !== '') ||
          (item.identifier_number && item.identifier_number.trim() !== '') ||
          (item.issuing_body_name && item.issuing_body_name.trim() !== '') ||
          (item.issuing_authority_other && item.issuing_authority_other.trim() !== '')
        )
        .map((item) => ({
          issuing_authority: item.issuing_authority || undefined,
          issuing_authority_other: item.issuing_authority === 'Other (Specify)'
            ? (item.issuing_authority_other || undefined)
            : undefined,
          identifier_number: item.identifier_number || undefined,
          issuing_body_name: item.issuing_body_name || undefined,
        }));

      // Prepare the payload with WHO registry data
      const payload = {
        ...existingData,
        who_registry: {
          primary_registry: {
            name: formData.primary_registry.name || undefined,
            unique_id_number: formData.primary_registry.unique_id_number || undefined,
          },
          registration_date: formData.registration_date || null,
          date_of_first_enrollment: formData.date_of_first_enrollment || null,
          secondary_identifiers: cleanedSecondary,
          sources_of_support: formData.sources_of_support || undefined,
          primary_sponsor: formData.primary_sponsor || undefined,
          secondary_sponsors: formData.secondary_sponsors || undefined,
          contact_for_public_queries: {
            email: formData.contact_for_public_queries.email || undefined,
            telephone: formData.contact_for_public_queries.telephone || undefined,
            postal_address: formData.contact_for_public_queries.postal_address || undefined,
            recruitment_status: formData.contact_for_public_queries.recruitment_status || undefined,
          },
          contact_for_scientific_queries: {
            principal_investigator: {
              name_and_title: formData.contact_for_scientific_queries.principal_investigator.name_and_title || undefined,
              email: formData.contact_for_scientific_queries.principal_investigator.email || undefined,
              telephone: formData.contact_for_scientific_queries.principal_investigator.telephone || undefined,
              postal_address: formData.contact_for_scientific_queries.principal_investigator.postal_address || undefined,
              affiliation: formData.contact_for_scientific_queries.principal_investigator.affiliation || undefined,
            },
            scientific_contact: {
              email: formData.contact_for_scientific_queries.scientific_contact.email || undefined,
              telephone: formData.contact_for_scientific_queries.scientific_contact.telephone || undefined,
              postal_address: formData.contact_for_scientific_queries.scientific_contact.postal_address || undefined,
              affiliation: formData.contact_for_scientific_queries.scientific_contact.affiliation || undefined,
            },
          },
          public_title: formData.public_title || undefined,
          scientific_title: formData.scientific_title || undefined,
          countries_of_recruitment: formData.countries_of_recruitment.length > 0 ? formData.countries_of_recruitment : undefined,
          health_conditions: formData.health_conditions || undefined,
          interventions: (formData.interventions || [])
            .filter((item) => item.name.trim() !== '' || item.description.trim() !== '')
            .map((item) => ({
              name: item.name || undefined,
              description: item.description || undefined,
            })),
          inclusion_criteria: formData.inclusion_criteria || undefined,
          exclusion_criteria: formData.exclusion_criteria || undefined,
          study_type: {
            type: formData.study_type.type || undefined,
            allocation_method: formData.study_type.allocation_method || undefined,
            masking: formData.study_type.masking || undefined,
            assignment: formData.study_type.assignment || undefined,
            purpose: formData.study_type.purpose || undefined,
            phase: formData.study_type.phase || undefined,
            allocation_concealment_and_sequence_generation:
              formData.study_type.allocation_concealment_and_sequence_generation || undefined,
          },
          sample_size_planned:
            formData.sample_size_planned !== '' ? Number(formData.sample_size_planned) : undefined,
          sample_size_enrolled:
            formData.sample_size_enrolled !== '' ? Number(formData.sample_size_enrolled) : undefined,
          recruitment_status: formData.recruitment_status_overall || undefined,
          primary_outcomes: (formData.primary_outcomes || [])
            .filter((item) =>
              item.name.trim() !== '' ||
              item.metric.trim() !== '' ||
              item.timepoint.trim() !== ''
            )
            .map((item) => ({
              name: item.name || undefined,
              metric: item.metric || undefined,
              timepoint: item.timepoint || undefined,
            })),
          secondary_outcomes: (formData.secondary_outcomes || [])
            .filter((item) =>
              item.name.trim() !== '' ||
              item.metric.trim() !== '' ||
              item.timepoint.trim() !== ''
            )
            .map((item) => ({
              name: item.name || undefined,
              metric: item.metric || undefined,
              timepoint: item.timepoint || undefined,
            })),
          ethics_review: {
            status: formData.ethics_review.status || undefined,
            approval_date: formData.ethics_review.approval_date || null,
            committee_details: formData.ethics_review.committee_details || undefined,
          },
          completion_date: formData.completion_date || null,
          summary_results: {
            results_posting_date: formData.summary_results.results_posting_date || null,
            first_publication_date: formData.summary_results.first_publication_date || null,
            results_urls: (formData.summary_results.results_urls_text || '')
              .split('\n')
              .map((u) => u.trim())
              .filter(Boolean),
            baseline_characteristics: formData.summary_results.baseline_characteristics || undefined,
            participant_flow: formData.summary_results.participant_flow || undefined,
            adverse_events: formData.summary_results.adverse_events || undefined,
            outcome_measures: formData.summary_results.outcome_measures || undefined,
            protocol_url: formData.summary_results.protocol_url || undefined,
            brief_summary: formData.summary_results.brief_summary || undefined,
          },
          ipd_sharing: {
            plan: formData.ipd_sharing.plan || undefined,
            description: formData.ipd_sharing.description || undefined,
          },
        },
      };

      await protocolRegistrationService.upsertByProject(projectId, payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save WHO registry data');
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Select or create a project to manage WHO registry registration details.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ py: 3, px: 2 }}>
    <Typography variant="h5" gutterBottom>
      WHO International Clinical Trials Registry Platform
    </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Track WHO ICTRP submissions, identifiers, and compliance checkpoints.
    </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Saved successfully.</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={handleAutoFill}
            disabled={loading || saving}
          >
            Auto-fill from Protocol
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={handleAutoPopulateFromCoreDatasheet}
            disabled={autoPopulating || loading || saving}
            title="Auto-populate from Core Datasheet"
          >
            {autoPopulating ? 'Auto-Populating...' : 'Auto-Populate from Core Datasheet'}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleExportXML}
            disabled={loading || saving}
          >
            Export XML
          </Button>
  </Box>
      </Stack>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
        1) Primary Registry and Trial Identifying Number
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name of the Primary Registry"
            value={formData.primary_registry.name}
            onChange={handlePrimaryRegistryChange('name')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., ClinicalTrials.gov, EudraCT, etc."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Unique ID Number"
            value={formData.primary_registry.unique_id_number}
            onChange={handlePrimaryRegistryChange('unique_id_number')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., NCT12345678"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
        2) Date of Registration in Primary Registry
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Date of Registration in Primary Registry"
            type="date"
            value={formData.registration_date}
            onChange={handleRegistrationDateChange}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        3) Secondary Identifying Numbers (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add any identifiers other than the Trial Identifying Number allocated by the Primary Registry. You may add more than one identifier.
      </Typography>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        {SECONDARY_AUTHORITY_OPTIONS.map((authority) => {
          const selected = (formData.secondary_identifiers || []).some(
            (item) => item.issuing_authority === authority
          );
          return (
            <Grid item xs={12} sm={6} md={4} key={authority}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selected}
                    onChange={handleAuthorityToggle(authority)}
                    disabled={loading || saving}
                    size="small"
                  />
                )}
                label={<Typography variant="body2">{authority}</Typography>}
              />
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(formData.secondary_identifiers || [])
          .filter((item) => item.issuing_authority)
          .map((item) => {
            const isOther = item.issuing_authority === 'Other (Specify)';
            return (
              <Grid item xs={12} md={6} key={item.issuing_authority}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {item.issuing_authority}
                  </Typography>
                  <Stack spacing={1.5}>
                    {isOther && (
                      <TextField
                        label="Specify other issuing authority"
                        value={item.issuing_authority_other}
                        onChange={handleSecondaryChange(item.issuing_authority, 'issuing_authority_other')}
                        fullWidth
                        size="small"
                        disabled={loading || saving}
                      />
                    )}
                    <TextField
                      label={getIdentifierLabel(item.issuing_authority)}
                      value={item.identifier_number}
                      onChange={handleSecondaryChange(item.issuing_authority, 'identifier_number')}
                      fullWidth
                      size="small"
                      disabled={loading || saving}
                      placeholder="e.g., NCT05891234"
                    />
                    <TextField
                      label="Issuing Body Name (optional)"
                      value={item.issuing_body_name}
                      onChange={handleSecondaryChange(item.issuing_authority, 'issuing_body_name')}
                      fullWidth
                      size="small"
                      disabled={loading || saving}
                      placeholder="e.g., ABC Oncology"
                    />
                  </Stack>
                </Box>
              </Grid>
            );
          })}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2, mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            4) Source(s) of Monetary or Material Support
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Major source(s) of monetary or material support for the trial (e.g. funding agency, foundation, company, institution).
          </Typography>
          <TextField
            label="Source(s) of Monetary or Material Support"
            value={formData.sources_of_support}
            onChange={handleSourcesOfSupportChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., NIH, ABC Pharmaceutical, XYZ Foundation"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            5) Primary Sponsor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The individual, organization, group or other legal entity which takes responsibility for initiating, managing and/or financing a study.
          </Typography>
          <TextField
            label="Primary Sponsor"
            value={formData.primary_sponsor}
            onChange={handlePrimarySponsorChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., University Hospital, ABC Pharma Inc."
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        6) Secondary Sponsor(s)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Additional individuals, organizations or other legal persons, if any, that have agreed with the primary sponsor to take on responsibilities of sponsorship.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Secondary Sponsor(s)"
            value={formData.secondary_sponsors}
            onChange={handleSecondarySponsorsChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., Co-Sponsor Organization, Partner Institution"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        7) Contact for Public Queries
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Contact information for the person who will respond to general public queries about the trial.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email Address"
            value={formData.contact_for_public_queries.email}
            onChange={handleContactForPublicQueriesChange('email')}
            fullWidth
            type="email"
            disabled={loading || saving}
            placeholder="e.g., contact@example.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Telephone Number"
            value={formData.contact_for_public_queries.telephone}
            onChange={handleContactForPublicQueriesChange('telephone')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., +1 (555) 123-4567"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Postal Address"
            value={formData.contact_for_public_queries.postal_address}
            onChange={handleContactForPublicQueriesChange('postal_address')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., 123 Main St, City, State, ZIP"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Current Recruitment Status"
            value={formData.contact_for_public_queries.recruitment_status}
            onChange={handleContactForPublicQueriesChange('recruitment_status')}
            fullWidth
            disabled={loading || saving}
          >
            {RECRUITMENT_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        8) Contact for Scientific Queries
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Scientific leadership and contact information for scientific queries about the trial.
      </Typography>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Principal Investigator
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name and Title"
            value={formData.contact_for_scientific_queries.principal_investigator.name_and_title}
            onChange={handlePrincipalInvestigatorChange('name_and_title')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., Dr. John Smith, MD, PhD"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email Address"
            value={formData.contact_for_scientific_queries.principal_investigator.email}
            onChange={handlePrincipalInvestigatorChange('email')}
            fullWidth
            type="email"
            disabled={loading || saving}
            placeholder="e.g., pi@institution.edu"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Telephone Number"
            value={formData.contact_for_scientific_queries.principal_investigator.telephone}
            onChange={handlePrincipalInvestigatorChange('telephone')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., +1 (555) 123-4567"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Postal Address"
            value={formData.contact_for_scientific_queries.principal_investigator.postal_address}
            onChange={handlePrincipalInvestigatorChange('postal_address')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., 123 University Ave"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Affiliation"
            value={formData.contact_for_scientific_queries.principal_investigator.affiliation}
            onChange={handlePrincipalInvestigatorChange('affiliation')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., University Hospital"
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Scientific Contact (if different from PI)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Email Address"
            value={formData.contact_for_scientific_queries.scientific_contact.email}
            onChange={handleScientificContactChange('email')}
            fullWidth
            type="email"
            disabled={loading || saving}
            placeholder="e.g., research@institution.edu"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Telephone Number"
            value={formData.contact_for_scientific_queries.scientific_contact.telephone}
            onChange={handleScientificContactChange('telephone')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., +1 (555) 987-6543"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Postal Address"
            value={formData.contact_for_scientific_queries.scientific_contact.postal_address}
            onChange={handleScientificContactChange('postal_address')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., Research Dept, 456 Lab St"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Affiliation"
            value={formData.contact_for_scientific_queries.scientific_contact.affiliation}
            onChange={handleScientificContactChange('affiliation')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., Research Institute"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        9) Public Title
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Title intended for the lay public in easily understood language.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Public Title"
            value={formData.public_title}
            onChange={handlePublicTitleChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., A study to test a new treatment for patients with advanced cancer"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        10) Scientific Title (Trial Acronym)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Scientific title of the study as it appears in the protocol submitted for funding and ethical review. Include trial acronym if available.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Scientific Title (Trial Acronym)"
            value={formData.scientific_title}
            onChange={handleScientificTitleChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., Phase III Randomized Study of XYZ in Advanced Cancer (ACRONYM-123)"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        11) Countries of Recruitment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The countries from which participants will be, are intended to be, or have been recruited at the time of registration.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Add Country"
            value={countryInput}
            onChange={(e) => setCountryInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddCountry();
              }
            }}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., United States"
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleAddCountry} disabled={loading || saving || !countryInput.trim()}>
                  <AddIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.countries_of_recruitment.map((country) => (
              <Chip
                key={country}
                label={country}
                onDelete={handleRemoveCountry(country)}
                deleteIcon={<CloseIcon />}
                disabled={loading || saving}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        12) Health Condition(s) or Problem(s) Studied
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Primary health condition(s) or problem(s) studied (e.g., depression, breast cancer, medication error). If the study is conducted in healthy volunteers, enter the health condition(s) being prevented.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Health Condition(s) or Problem(s) Studied"
            value={formData.health_conditions}
            onChange={handleHealthConditionsChange}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., Advanced Non-Small Cell Lung Cancer, Metastatic Breast Cancer"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        13) Intervention(s)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        For each arm of the trial, record intervention name and description. Use generic names for drugs (International Non-Proprietary Name if possible). For controls, enter "placebo" or "no treatment" as applicable.
      </Typography>
      
      <Stack spacing={2} sx={{ mb: 2 }}>
        {formData.interventions.map((intervention, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Intervention #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Intervention Name"
                  value={intervention.name}
                  onChange={handleInterventionChange(index, 'name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., Pembrolizumab, Placebo"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Intervention Description"
                  value={intervention.description}
                  onChange={handleInterventionChange(index, 'description')}
                  fullWidth
                  multiline
                  rows={1}
                  disabled={loading || saving}
                  placeholder="e.g., 200mg IV every 3 weeks for 24 months"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  color="error"
                  size="small"
                  onClick={handleRemoveIntervention(index)}
                  disabled={loading || saving || formData.interventions.length === 1}
                >
                  Remove Intervention
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      <Button
        variant="outlined"
        onClick={handleAddIntervention}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Another Intervention
      </Button>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        14) Key Inclusion and Exclusion Criteria
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Inclusion and exclusion criteria for participant selection, including age and sex. For studies in healthy volunteers not belonging to the target population, enter "healthy human volunteer".
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Inclusion Criteria"
            value={formData.inclusion_criteria}
            onChange={handleInclusionCriteriaChange}
            fullWidth
            multiline
            rows={4}
            disabled={loading || saving}
            placeholder="e.g., Age ≥18 years, Histologically confirmed diagnosis, ECOG performance status 0-1"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Exclusion Criteria"
            value={formData.exclusion_criteria}
            onChange={handleExclusionCriteriaChange}
            fullWidth
            multiline
            rows={4}
            disabled={loading || saving}
            placeholder="e.g., Prior systemic therapy, Active infection, Pregnant or nursing women"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        15) Study Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Study type and design, including allocation method, masking, assignment, purpose, phase, and (for randomized trials) allocation concealment and sequence generation.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Type of Study"
            value={formData.study_type.type}
            onChange={handleStudyTypeChange('type')}
            fullWidth
            disabled={loading || saving}
          >
            {STUDY_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Allocation Method"
            value={formData.study_type.allocation_method}
            onChange={handleStudyTypeChange('allocation_method')}
            fullWidth
            disabled={loading || saving}
          >
            {ALLOCATION_METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Assignment"
            value={formData.study_type.assignment}
            onChange={handleStudyTypeChange('assignment')}
            fullWidth
            disabled={loading || saving}
          >
            {ASSIGNMENT_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Masking"
            value={formData.study_type.masking}
            onChange={handleStudyTypeChange('masking')}
            fullWidth
            disabled={loading || saving}
          >
            {MASKING_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Purpose"
            value={formData.study_type.purpose}
            onChange={handleStudyTypeChange('purpose')}
            fullWidth
            disabled={loading || saving}
          >
            {PURPOSE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Phase (if applicable)"
            value={formData.study_type.phase}
            onChange={handleStudyTypeChange('phase')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., Phase 1, Phase 2/3, Not Applicable"
          />
        </Grid>
        {formData.study_type.allocation_method === 'Randomized' && (
          <Grid item xs={12}>
            <TextField
              label="Allocation Concealment and Sequence Generation (for randomized trials)"
              value={formData.study_type.allocation_concealment_and_sequence_generation}
              onChange={handleStudyTypeChange('allocation_concealment_and_sequence_generation')}
              fullWidth
              multiline
              rows={3}
              disabled={loading || saving}
              placeholder="e.g., Central randomization using computer-generated sequence; permuted blocks stratified by site"
            />
          </Grid>
        )}
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
        16) Date of First Enrollment
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Date of First Enrollment (anticipated or actual)"
            type="date"
            value={formData.date_of_first_enrollment}
            onChange={handleFirstEnrollmentDateChange}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        17) Sample Size
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Number of participants the trial plans to enrol in total and number actually enrolled at the time of registration/update.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Planned Total Sample Size"
            value={formData.sample_size_planned}
            onChange={handleSampleSizeChange('sample_size_planned')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., 250"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Currently Enrolled Sample Size"
            value={formData.sample_size_enrolled}
            onChange={handleSampleSizeChange('sample_size_enrolled')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., 120"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        18) Recruitment Status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Recruitment status of this trial.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Overall Recruitment Status"
            value={formData.recruitment_status_overall}
            onChange={handleRecruitmentStatusOverallChange}
            fullWidth
            disabled={loading || saving}
          >
            {RECRUITMENT_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        19) Primary Outcome(s)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The main outcome(s) used to determine the effects of the intervention(s). For each outcome, provide name (no abbreviations), metric/method of measurement, and timepoint(s) of primary interest.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {formData.primary_outcomes.map((outcome, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Primary Outcome #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Outcome Name"
                  value={outcome.name}
                  onChange={handlePrimaryOutcomeChange(index, 'name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., Depression"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Metric / Method of Measurement"
                  value={outcome.metric}
                  onChange={handlePrimaryOutcomeChange(index, 'metric')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., Beck Depression Score"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Timepoint(s)"
                  value={outcome.timepoint}
                  onChange={handlePrimaryOutcomeChange(index, 'timepoint')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., 18 weeks following end of treatment"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  color="error"
                  size="small"
                  onClick={handleRemovePrimaryOutcome(index)}
                  disabled={loading || saving || formData.primary_outcomes.length === 1}
                >
                  Remove Primary Outcome
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      <Button
        variant="outlined"
        onClick={handleAddPrimaryOutcome}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Another Primary Outcome
      </Button>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        20) Key Secondary Outcomes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Outcomes of secondary interest or measured at timepoints of secondary interest. For each secondary outcome, provide name, metric/method of measurement, and timepoint(s) of interest.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {formData.secondary_outcomes.map((outcome, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Secondary Outcome #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Outcome Name"
                  value={outcome.name}
                  onChange={handleSecondaryOutcomeChange(index, 'name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., Anxiety"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Metric / Method of Measurement"
                  value={outcome.metric}
                  onChange={handleSecondaryOutcomeChange(index, 'metric')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., Hamilton Anxiety Rating Scale"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Timepoint(s)"
                  value={outcome.timepoint}
                  onChange={handleSecondaryOutcomeChange(index, 'timepoint')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g., 12 weeks after randomization"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  color="error"
                  size="small"
                  onClick={handleRemoveSecondaryOutcome(index)}
                  disabled={loading || saving || formData.secondary_outcomes.length === 1}
                >
                  Remove Secondary Outcome
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      <Button
        variant="outlined"
        onClick={handleAddSecondaryOutcome}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Another Secondary Outcome
      </Button>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        21) Ethics Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Ethics review information for this trial.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Ethics Review Status"
            value={formData.ethics_review.status}
            onChange={handleEthicsReviewChange('status')}
            fullWidth
            disabled={loading || saving}
          >
            {['Not approved', 'Approved', 'Not Available'].map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Date of Approval"
            type="date"
            value={formData.ethics_review.approval_date}
            onChange={handleEthicsReviewChange('approval_date')}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Name and Contact Details of Ethics Committee(s)"
            value={formData.ethics_review.committee_details}
            onChange={handleEthicsReviewChange('committee_details')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="e.g., Institutional Ethics Committee, address, phone, email"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        22) Completion Date
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Date of study completion (last subject, last visit).
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Date of Study Completion"
            type="date"
            value={formData.completion_date}
            onChange={handleCompletionDateChange}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        23) Summary Results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Summary of trial results and related information.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Date of Posting of Results Summaries"
            type="date"
            value={formData.summary_results.results_posting_date}
            onChange={handleSummaryResultsChange('results_posting_date')}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Date of First Journal Publication of Results"
            type="date"
            value={formData.summary_results.first_publication_date}
            onChange={handleSummaryResultsChange('first_publication_date')}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Results / Publications URLs (one per line)"
            value={formData.summary_results.results_urls_text}
            onChange={handleSummaryResultsChange('results_urls_text')}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="e.g., https://journal.org/article123"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Baseline Characteristics"
            value={formData.summary_results.baseline_characteristics}
            onChange={handleSummaryResultsChange('baseline_characteristics')}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="Summary of baseline characteristics by arm/group"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Participant Flow"
            value={formData.summary_results.participant_flow}
            onChange={handleSummaryResultsChange('participant_flow')}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="Narrative or tabular description of participant flow"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Adverse Events"
            value={formData.summary_results.adverse_events}
            onChange={handleSummaryResultsChange('adverse_events')}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="Summary of serious and other adverse events"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Outcome Measures (tables / key results)"
            value={formData.summary_results.outcome_measures}
            onChange={handleSummaryResultsChange('outcome_measures')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="Key results for each primary and secondary outcome, including measures of precision"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="URL Link to Protocol File(s) with Version and Date"
            value={formData.summary_results.protocol_url}
            onChange={handleSummaryResultsChange('protocol_url')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g., https://trialsite.org/protocol_v3_2024-01-15.pdf"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Brief Summary of Results"
            value={formData.summary_results.brief_summary}
            onChange={handleSummaryResultsChange('brief_summary')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="Short narrative summary of main trial findings"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        24) IPD Sharing Statement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Plan for sharing de-identified individual participant data (IPD).
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Plan to Share IPD"
            value={formData.ipd_sharing.plan}
            onChange={handleIpdSharingChange('plan')}
            fullWidth
            disabled={loading || saving}
          >
            {['Yes', 'No'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="IPD Sharing Plan Description"
            value={formData.ipd_sharing.description}
            onChange={handleIpdSharingChange('description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="Whether IPD will be shared, what will be shared, when, by what mechanism, with whom, and for what analyses."
          />
        </Grid>
      </Grid>

      {/* Snackbar for auto-population messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WHORegistry;
